import { Grid, Typography, useTheme } from '@hermes/web-components'
import { Search, Dropdown } from '@hermes/design-system'
import { SearchReviewsFilters } from '../../providers/SearchReviews/types'
import { SearchSpecialistsFilters } from '../../providers/SearchSpecialists/types'
import { useTimeout } from '../../hooks/useTimeout'
import { SearchPeerRecommendationsFilters } from '../../providers/SearchPeerRecommendations/types'
import { SortOptions } from '../../types'
import { GridHidden } from '../GridHidden'
import { searchInput, dropdown } from './styles.css'
import { FC, useEffect, useMemo, useState } from 'react'

type SearchFieldProps = {
  changeSort: (sort: string) => void
  changeSearch: (search: string) => void
  isInitial?: boolean
  filters: SearchReviewsFilters | SearchSpecialistsFilters | SearchPeerRecommendationsFilters
  placeholder: string
  sortPlaceholder?: string
  sortOptions: SortOptions[]
  titleCount?: string
}

type SortSelectProps = {
  value: string | null
  onChangeSort: (val: string) => void
  sortOptions: SortOptions[]
  placeholder?: string
}

const SortSelect: FC<SortSelectProps> = ({ value, onChangeSort, sortOptions, placeholder }) => {
  const selectedValue = useMemo(() => {
    return sortOptions.find((option) => option.value === value)?.name || placeholder || ''
  }, [sortOptions, value, placeholder])

  return (
    <Dropdown value={selectedValue} onChange={onChangeSort} placeholder={placeholder} className={dropdown}>
      {sortOptions.map((sortOption) => (
        <Dropdown.Item key={`${sortOption.name}-${sortOption.value}`} value={sortOption.value}>
          {sortOption.name}
        </Dropdown.Item>
      ))}
    </Dropdown>
  )
}

const SearchField = ({
  changeSort,
  changeSearch,
  isInitial,
  filters,
  placeholder,
  sortPlaceholder,
  sortOptions,
  titleCount
}: SearchFieldProps) => {
  const timeout = useTimeout()
  const theme = useTheme()
  const [searchValue, setSearchValue] = useState('')

  const onChangeSearch = (val: string) => {
    setSearchValue(val)
    timeout(() => changeSearch(val))
  }

  const onChangeSort = (val: string) => {
    timeout(() => changeSort(val), 1000)
  }

  useEffect(() => {
    if (isInitial) {
      setSearchValue('')
    }
  }, [isInitial])

  return (
    <Grid container item spacing={2}>
      <Grid container item xs={12} alignItems="center">
        <Grid item sm={7} xs={12}>
          <Search value={searchValue} placeholder={placeholder} onChange={onChangeSearch} className={searchInput}>
            <Search.Input />
            <Search.Icon />
          </Search>
        </Grid>
        <GridHidden down="sm" xs={5} item display="flex" justifyContent="end">
          <SortSelect
            placeholder={sortPlaceholder}
            onChangeSort={onChangeSort}
            sortOptions={sortOptions}
            value={filters.sort}
          />
        </GridHidden>
      </Grid>
      <Grid container item xs={12} alignItems="center">
        {titleCount && (
          <Grid item xs>
            <Typography
              variant="body2"
              color="text.darkNavy"
              sx={theme.custom.SpecialistPage.endorsements.endorsementsCountText}
            >
              {titleCount}
            </Typography>
          </Grid>
        )}
        <GridHidden up="sm" item>
          <SortSelect
            placeholder={sortPlaceholder}
            onChangeSort={onChangeSort}
            sortOptions={sortOptions}
            value={filters.sort}
          />
        </GridHidden>
      </Grid>
    </Grid>
  )
}

export default SearchField
